<template>
    <div @click="handleUserInteraction" @touchstart="handleUserInteraction">
      <Background />
      <!-- Hide HomeMenu if login or signup dialog is shown -->
      <HomeMenu v-if="!showLoginDialog && !showSignUpDialog" />
      <div
        v-if="isLoading"
        class="loader-overlay"
        :class="{ 'fade-out': !isLoading }"
      >
        <IntroLoader />
        <PrimBtn class="skip-btn" @click="skipLoader"></PrimBtn>
  
        <audio ref="skipAudio">
          <source src="../assets/audio/click.mp3" type="audio/mp3" />
          Your browser does not support the audio element.
        </audio>
      </div>
  
      <!-- Inside the Login Dialog -->
      <!-- Login Dialog -->
      <v-dialog
        v-if="!isLoggedIn"
        style="z-index: 10"
        persistent
        v-model="showLoginDialog"
        max-width="390"
      >
        <template v-slot:default>
          <v-card class="bg-card" elevation="16" rounded="lg">
            <v-card-title class="text-center bg-ct">
              <h3 class="py-2">Login</h3>
            </v-card-title>
            <v-card-text class="p-0">
              <v-form ref="loginForm" v-model="valid" class="px-8 pt-8">
                <v-text-field
                  v-model="loginEmail"
                  density="compact"
                  placeholder="Email"
                  type="email"
                  rounded="lg"
                  variant="outlined"
                  style="line-height: 30px"
                  color="yellow-darken-1"
                  required
                >
                  <template v-slot:prepend-inner>
                    <v-icon>mdi-email</v-icon>
                  </template>
                </v-text-field>
                <v-text-field
                  v-model="loginPassword"
                  :append-inner-icon="
                    isLoginPasswordVisible ? 'mdi-eye-off' : 'mdi-eye'
                  "
                  :type="isLoginPasswordVisible ? 'text' : 'password'"
                  placeholder="Password"
                  rounded="lg"
                  density="compact"
                  variant="outlined"
                  style="line-height: 30px"
                  color="yellow-darken-1"
                  @click:append-inner="
                    isLoginPasswordVisible = !isLoginPasswordVisible
                  "
                  required
                >
                  <template v-slot:prepend-inner>
                    <v-icon>mdi-lock</v-icon>
                  </template>
                </v-text-field>
  
                <v-btn
                  variant="tonal"
                  color="yellow-darken-1"
                  elevation="24"
                  @click="login"
                  class="px-6 mb-4"
                  size="large"
                  block
                  >Login</v-btn
                >
  
                <v-divider>or</v-divider>
  
                <!-- Login as Guest Card -->
                <v-card
                  class="bg-btn mt-4"
                  @click="loginAsGuest"
                  elevation="2"
                  rounded="lg"
                >
                  <v-card-title class="d-flex align-center justify-center py-1">
                    <v-icon color="yellow-darken-1" class="mr-2"
                      >mdi-account</v-icon
                    >
                    <span class="text-button">Login as Guest</span>
                  </v-card-title>
                </v-card>
  
                <!-- Login whit Google -->
                <v-card class="bg-btn mt-4" @click="googleAuth" elevation="2" rounded="lg">
                  <v-card-title class="d-flex align-center justify-center py-1">
                    <GoogleIcon />
                    <span class="text-button">Login with Google</span>
                  </v-card-title>
                </v-card>
  
                <v-card-text class="text-center my-2">
                  <span class="text-center font-weight-light">
                    Don't have an account?
                  </span>
                  <span
                    class="text-yellow-darken-1 text-decoration-none cursor-pointer"
                    @click="showSignUpDialog = true"
                  >
                    Sign up
                  </span>
                </v-card-text>
              </v-form>
            </v-card-text>
          </v-card>
        </template>
      </v-dialog>
  
      <!-- Sign Up Dialog -->
      <v-dialog v-if="!isLoggedIn" v-model="showSignUpDialog" max-width="390">
        <template v-slot:default>
          <!-- <v-img src="@/assets/logo.png" height="200"></v-img> -->
          <v-card class="bg-card" elevation="16" rounded="lg">
            <v-card-title class="text-center bg-ct">
              <h3 class="py-2">Sign Up</h3>
            </v-card-title>
            <v-card-text class="p-0">
              <v-form ref="signupForm" v-model="valid" class="px-8 pt-8">
                <v-text-field
                  v-model="signupEmail"
                  density="compact"
                  placeholder="Email"
                  type="email"
                  rounded="lg"
                  variant="outlined"
                  style="line-height: 30px"
                  color="yellow-darken-1"
                  :rules="[emailRules]"
                  @focus="isSignupEmailFocused = true"
                  @blur="isSignupEmailFocused = false"
                  required
                >
                  <template v-slot:prepend-inner>
                    <v-icon :color="isSignupEmailFocused ? 'yellow-darken-1' : ''"
                      >mdi-email</v-icon
                    >
                  </template></v-text-field
                >
                <v-text-field
                  v-model="signupUsername"
                  placeholder="Username"
                  rounded="lg"
                  density="compact"
                  variant="outlined"
                  style="line-height: 30px"
                  color="yellow-darken-1"
                  @focus="isSignupUsernameFocused = true"
                  @blur="isSignupUsernameFocused = false"
                  required
                >
                  <template v-slot:prepend-inner>
                    <v-icon
                      :color="isSignupUsernameFocused ? 'yellow-darken-1' : ''"
                      >mdi-account</v-icon
                    >
                  </template></v-text-field
                >
                <v-text-field
                  v-model="signupPassword"
                  :append-inner-icon="
                    isSignupPasswordVisible ? 'mdi-eye-off' : 'mdi-eye'
                  "
                  :type="isSignupPasswordVisible ? 'text' : 'password'"
                  placeholder="Password"
                  type="password"
                  rounded="lg"
                  density="compact"
                  variant="outlined"
                  style="line-height: 30px"
                  color="yellow-darken-1"
                  @focus="isSignupPasswordFocused = true"
                  @blur="isSignupPasswordFocused = false"
                  @click:append-inner="
                    isSignupPasswordVisible = !isSignupPasswordVisible
                  "
                  required
                >
                  <template v-slot:prepend-inner>
                    <v-icon
                      :color="isSignupPasswordFocused ? 'yellow-darken-1' : ''"
                      >mdi-lock</v-icon
                    >
                  </template></v-text-field
                >
              </v-form>
            </v-card-text>
            <v-card-actions class="d-flex justify-content-center pb-7">
              <v-btn
                variant="tonal"
                color="grey-lighten-1"
                @click="showSignUpDialog = false"
                elevation="24"
              >
                Cancel
              </v-btn>
  
              <v-btn
                variant="tonal"
                color="yellow-darken-1"
                @click="signup"
                elevation="24"
              >
                Create Account
              </v-btn>
            </v-card-actions>
          </v-card>
        </template>
      </v-dialog>
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted, computed } from "vue";
  import Background from "@/components/landingpage/Background.vue";
  import IntroLoader from "@/pages/loader.vue";
  import PrimBtn from "@/components/buttons/PrimBtn.vue";
  import SecBtn from "@/components/buttons/SecBtn.vue";
  import { useUserStore } from "@/stores/useUserStore";
  import { useRouter } from "vue-router";
  import { useAudioStore } from "@/stores/audioStore";
  import { supabase } from "@/lib/supabase";
  import { useToast } from "vue-toastification";
  
  const audioStore = useAudioStore();
  const isLoading = ref(true);
  const audioPlayer = ref(null);
  const skipAudio = ref(null);
  const hasPlayedAudio = ref(false);
  const userStore = useUserStore();
  const valid = ref(false);
  const toast = useToast();
  const router = useRouter();
  const signupEmail = ref("");
  const signupUsername = ref("");
  const signupPassword = ref("");
  const loginEmail = ref("");
  const loginPassword = ref("");
  
  const showSignUpDialog = ref(false);
  const showLoginDialog = ref(false);
  
  const isLoginEmailFocused = ref(false);
  const isLoginPasswordFocused = ref(false);
  
  const isSignupEmailFocused = ref(false);
  const isSignupUsernameFocused = ref(false);
  const isSignupPasswordFocused = ref(false);
  
  const isLoginPasswordVisible = ref(false);
  const isSignupPasswordVisible = ref(false);
  
  // Computed property to check if the user is already logged in
  const isLoggedIn = computed(() => userStore.isAuthenticated);
  
  // Function to skip the loader and show the login dialog if not logged in
  const skipLoader = () => {
    if (skipAudio.value) {
      skipAudio.value
        .play()
  
        .then(() => {
          setTimeout(() => {
            isLoading.value = false;
            if (!isLoggedIn.value) showLoginDialog.value = true;
          }, 500);
          audioStore.playAdal();
        })
        .catch((error) => {
          console.error("Error playing skip audio:", error);
        });
    }
  };
  
  const handleUserInteraction = () => {
    // Play click sound when user interacts with the component
    if (!hasPlayedAudio.value) {
      audioStore.playAdal(); // Ensure adal sound plays once on mount
      hasPlayedAudio.value = true;
    }
  };
  
  onMounted(() => {
    loginAsGuest();
  });
  

  // Function to populate guest credentials and login
  const loginAsGuest = async () => {
    try {
      // Direct sign-in with Supabase
      const { data, error } = await supabase.auth.signInWithPassword({
        email: "villain@gmail.com",
        password: "villain090231/.",
      });
  
      if (error) {
        console.error("Login error:", error.message);
        toast.error(error.message); // Show toast error message
        throw new Error(error.message);
      }
  
      if (data.session) {
        const { session, user } = data;
  
        // Storing tokens in localStorage
        localStorage.setItem("access_token", session.access_token);
        localStorage.setItem("refresh_token", session.refresh_token);
        localStorage.setItem("auth_id", user.id);
  
        // Fetching user profile from 'users' table
        const { data: profiles, error: profileError } = await supabase
          .from("users")
          .select("*")
          .eq("user_id", user.id);
  
        if (profileError || profiles.length === 0) {
          console.error("Profile fetch error:", profileError?.message);
          toast.error("Profile fetch error."); // Show toast error for profile
          throw new Error("Profile fetch error.");
        }
  
        // Storing profile information in localStorage
        localStorage.setItem("user_id", profiles[0].id); // Adjust if `user_id` differs
        localStorage.setItem("Role", profiles[0].is_admin ? "true" : "false");
  
        // Update Pinia store with user information
        userStore.userId = user.id;
        userStore.username = profiles[0].user_name;
  
        toast.success("Login successful!"); // Show toast success message
        router.push({ name: "landing" });
        return { user: data.user };
  
        
      }
    } catch (error) {
      console.error("LoginAsGuest error:", error.message);
      toast.error("Login failed.");
    }
  };
  </script>
  
  <style scoped>
  #ygar {
    background-color: #151515;
    position: relative;
  }
  
  .loader-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(21, 21, 21, 0.9); /* Dark semi-transparent overlay */
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000; /* Ensure it's on top of all content */
    transition: opacity 0.5s ease-in-out;
  }
  
  .loader-overlay.fade-out {
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.5s ease-in-out, visibility 0s 0.5s; /* Visibility hidden after fade-out */
  }
  
  .skip-btn {
    position: absolute;
    bottom: 20px;
    right: 20px;
  }
  
  .bg-card {
    background: rgba(0, 0, 0, 0.67);
    border-radius: 16px;
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(8.6px);
    -webkit-backdrop-filter: blur(8.6px);
    border: 1px solid rgba(0, 0, 0, 0.06);
  }
  
  .bg-ct {
    background: rgb(0, 0, 0);
    background: radial-gradient(
      circle,
      rgba(0, 0, 0, 1) 0%,
      rgba(85, 85, 85, 0) 100%
    );
  }
  
  .bg-btn {
    background: rgb(0, 0, 0);
    background: radial-gradient(
      circle,
      rgba(0, 0, 0, 1) 0%,
      rgba(85, 85, 85, 0) 115%
    );
  }
  
  .cursor-pointer {
    cursor: pointer;
  }
  
  :deep(.v-overlay__scrim) {
    background-color: rgba(
      255,
      193,
      7,
      0.8
    ) !important; /* Change to your desired color */
    opacity: var(--v-overlay-opacity, 0.15) !important;
  }
  </style>
  